/**
 * Renders content in the nav pane, display pane or a dialog.  The main purpose
 * of this class is to ensure we cleanup old views correctly.  Example usage:
 *
 * Usage:
 *
 * <pre>
 * var view = new Backbone.View()
 * appDisplay.dialog(view)
 * </pre>
 */

import $ from 'jquery'

import _ from 'lodash'
import Backbone from 'backbone'
import conductor from 'scoreboard/components/conductor'
import Dialog from 'scoreboard/components/dialog'
import dialogStack from './dialog-stack'
import autofocus from 'scoreboard/polyfills/autofocus'
import spectator from 'scoreboard/components/spectator'
import logPath from 'scoreboard/components/log-path'
import loadingDots from 'scoreboard/components/loading-dots.hbs'
import constants from 'scoreboard/components/constants'
import ExportPreparer from 'scoreboard/components/export/export-preparer'
import layoutManager from 'scoreboard/components/layout-manager'

var Display = function(options) {
	this.options = options
}

_.extend(Display.prototype, Backbone.Events, {

	// the currently rendered view
	view: null,

	// Internal stack containing view paths
	viewHistory: [],

	detach: function() {
		delete this._el
		_.invoke(this.view, 'remove')
		delete this.view
		delete this.previousView
	},

	el: function() {
		return this._el || (this._el = $(this.options.selector))
	},

	/**
	 * The promise can be one of
	 * (1) a jQuery promise
	 * (2) a jqXHR object
	 * (3) a normal promise
	 *
	 * @param {Promise} promise
	 *
	 * @returns the provided promise, or a new Deferred object that implements abort()
	 */
	_decoratePromiseWithAbortFunction: function (promise) {
		let def = promise
		if (!_.isFunction(promise.abort)) {
			//we're dealing with a promise not a jqXHR object, sub with a
			//deferred that can be aborted  #8468
			def = new $.Deferred()
			def.abort = function () {
				this.reject({aborted: true})
			}

			//when the underlying deferred is resolved, only resolve the
			//shim if we didn't abort prior to the underlying deferred finishing
			promise.done(function () {
				def.state() == 'pending' ?
					def.resolve.apply(this, arguments) :
					def.reject()
			}.bind(this))

			// And make sure failures are propagated as well so the view
			// is properly restored
			promise.fail(function () {
				def.reject.apply(this, arguments)
			}.bind(this))
		}
		return def
	},

	_cleanupPreviousRequest: function (viewOrPromise) {
		// cancel any old requests that might still be running
		_.result(this.promise, 'abort')
		// clear any loading timeouts that might be around
		viewOrPromise && viewOrPromise.__loadingTimeout__ &&
		clearTimeout(viewOrPromise.__loadingTimeout__)
	},

	//if a promise defer cleanup until after the promise is resolved
	_cleanupCurrentView: function(options = {}) {
		if (this.view && this.view.remove) {
			// Make sure the view we're removing isn't this.view anymore,
			// in case it does anything that triggers a window resize event
			// (like EditableCanvas). Save it as this.previousView to be removed later.

			if (options.delayViewRemove) {
				this.previousView = this.view
				delete this.view
			} else {
				let v = this.view
				delete this.view
				v.remove()
			}
		}
	},

	/**
	 * For nav views, we don't want to clear out the existing nav until we know
	 * if the gardener's XHR was successful.
	 *
	 * Navs do not pass promises, just a view with the Gardener doing the XHR itself
	 *
	 * @param {Nav} view
	 */
	displayNav: function(view, options = {}) {
		const showView = () => {
			this._cleanupCurrentView()
			this._setView(view, true)
			Backbone.trigger('nav:success')
		}

		const {drilldown = false} = options
		this._cleanupPreviousRequest(view)
		if (!this.view || !drilldown) {
			showView()
		} else {
			this.doDrilldownNav(view, showView)
		}
		return view
	},

	/**
	 * Moving to a new nav via a drill down so show loading dots on current nav
	 * until we get the Xhr's response code and can make a decision about
	 * whether or not to render the new nav or leave user where they are.
	 *
	 * But, only if we are making a new tree-stream request; hence the 'stream' event
	 * e.g. moving from one scorecard to another in the same org (through related items)
	 * does not required the nav to reload
	 *
	 * @param newNavView
	 * @param {Function} onSuccess called if the nav was successful
	 */
	doDrilldownNav: function (newNavView, onSuccess) {

		newNavView.once('stream', () => {
			_.invoke(this.view, 'showLoadingDots')
		})

		newNavView.once('success', () => {
			_.invoke(this.view, 'clearLoadingDots')
			onSuccess && onSuccess()
		})

		newNavView.once('error', () => {
			_.invoke(this.view, 'clearLoadingDots')
		})
	},

	/**
	 *
	 * @param viewOrPromise
	 * @param [viewCreator]
	 *
	 * @param {Boolean} [options.delayViewRemove=false]
	 * @param {Boolean} options.drilldown
	 *
	 * @returns {Promise<T>|undefined} A Promise that will be resolved with
	 * the view (if a promise was passed to this function), or undefined if a
	 *     view was passed
	 */
	display: function(viewOrPromise, viewCreator, options = {}) {

		const {drilldown} = options
		this._cleanupPreviousRequest(viewOrPromise)

		if (viewOrPromise && _.isFunction(viewOrPromise.then)) {
			this.promise = this._decoratePromiseWithAbortFunction(viewOrPromise)

			if (viewOrPromise.state() == 'pending') {
				viewOrPromise.__loadingTimeout__ = setTimeout(function () {
					//hide the current view
					this.el().children().addClass('is-not-displayed')
					//append and not replace so we can revert back to the current
					//view if the load failed
					$('<div class="loading-dots-container"></div>')
						.appendTo(this.el()).html(loadingDots())
				}.bind(this), constants.LOADING_TIME)
			}
			return this.promise.always(function() {
				// don't show loading dots unless the wait exceeds
				// constants.LOADING_TIME.  it's important this goes BEFORE
				// creating the view or loading dots can end up replacing the
				// view
				clearTimeout(viewOrPromise.__loadingTimeout__)
				delete viewOrPromise.__loadingTimeout__
				//remove previous loading dots if any
				this.el().find('.loading-dots-container').remove()
			}.bind(this)).then(function() {
				this._cleanupCurrentView()
				let view
				if (viewCreator) {
					view = viewCreator.apply(this, arguments)
					this._setView(view)
				}
				// no need to keep these around anymore
				if (this.promise && this.promise.state() != 'pending') {
					//#9917 we MUST not delete the promise if it hasn't been completed
					//otherwise subsequent display calls will fail to abort previous
					//promise and stale views can show
					delete this.promise
				}
				// export-preparer and bookmarks-overview need the view to
				// listen for events
				return view
			}.bind(this)).fail((e) => {
				//if promise failed (but not aborted by another req), reshow current view
				if (e && !e.aborted) {
					this.el().children().removeClass('is-not-displayed')
					if (drilldown && !_.isNil(_previousUrl)) {
						conductor.navigate(_previousUrl, {
							// Don't record the current url as the previous
							// since we're resetting the url do to a failure
							silent: true,
							replace: true
						})
					}
				}
				if (e && e.stack) {
					// note: I was tempted to put in a call to debugger; here,
					// but because of the way promises work it doesn't bring
					// you to the line of code where the error actually happened
					// so it's not helpful
					console.trace(e.stack)
				}
			})
		} else {
			this._cleanupCurrentView(options)
			this._setView(viewOrPromise)
			delete this.promise
			return viewOrPromise
		}
	},

	resize: function () {
		this.view && this.view.trigger && this.view.trigger('resize')
	},

	_setView: function(view, viewIsReady) {
		// If there was a view shown before this one, call remove() on it before
		// adding in the new view. We wait to remove the previous view until here
		// in order to lessen the time between destroying the old view and inserting the
		// new one, thus lessening any visible flicker.
		_.invoke(this.previousView, 'remove')
		this.view = view

		if (view && view.constructor) {
			this.viewHistory.unshift(view.constructor.__filename__)
		}

		logPath(this.options.name, view)

		if (view && view.render) {
			this.el().html(view.el)

			const onReady = options => { // leave multiple lines for easier debugging
				this.trigger('ready', view, options)
			}
			// It's possible that a stream could fully load and the view be ready
			// before appDisplay.display() is invoked.  In this scenario we must
			// emit 'ready', even if the view is marked deferred (#26230),
			// because the listener is setup too late and will never be invoked
			const triggerReadyEventNow = !view.deferredReady || view.fullyLoaded

			if (!triggerReadyEventNow) {
				// If the view is not ready immediately (deferredReady), wait for the
				// view's ready event before raising the display's ready event
				// this MUST go before view.render() or it might not get invoked
				view.on('ready', onReady)
			}
			view.render()
			if (triggerReadyEventNow) {
				onReady()
			}
		} else {
			// jQuery will only actually change the contents if html is defined.
			// this is the behavior we want or else we have race conditions that
			// sometimes prevent bookmarks and briefings from rendering correctly
			// (since in their overviews they invoke other routes that will end up
			// routing through this method again with the real stuff we want in the
			// display).  If you want to clear out the display, just pass in an
			// empty string
			this.el().html(view)
		}
		autofocus(this.el())

		// #10959 give bookmarks a chance to un-register themselves as the
		// spectator before changing the main nav
		process.nextTick(() => {
			// Don't suppress slide controls or update the section
			// until we confirm we actually loaded the display pane successfully.
			Backbone.trigger('route:success', viewIsReady)
		})
	}


})

var appDisplay = {

	_nav: new Display({ selector: 'nav.nav-pane .content', name: 'nav' }),

	_display: new Display({ selector: 'section.display-pane', name: 'display' }),

	_slide: new Display({ selector: '.slide-pane', name: 'slide' }),

	detach: function() {
		_.invokeMap([this._nav, this._display, this._slide], 'detach')
	},

	setDrilldown: function() {
		this.drilldown = true
	},

	/**
	 * @returns {Boolean} True if the user's last navigation attempt was the
	 * result of a drill down.
	 */
	isDrilldown: function() {
		return this.drilldown
	},

	// retrieves the nav pane's dom element
	navEl: function() {
		return this._nav.el()
	},

	// retrieves the display pane's dom element
	displayEl: function() {
		return this._display.el()
	},

	/**
	 * replaces the view in the nav pane with the provided view
	 *
	 * @param ViewClass
	 * @param {Object} [options] passed to view constructor
	 * @param {Boolean} [options.requireSuccess] true indicates that we can't
	 * clear out the nav until the new nav is known to be successful (e.g. drilldowns)
	 *
	 * @returns {Promise<Nav>} fulfilled with the instance of `ViewClass`
	 */
	nav: function(ViewClass, options = {}) {
		// handle clearing out the view
		if (!ViewClass) {
			this._nav.display()
		}

		return new Promise(resolve => {
			const newSection = ViewClass && ViewClass.prototype.section
			const oldSection = this._nav.view ? this._nav.view.section : null
			const currentView = this._nav.view

			const navToNewSection = (!spectator.getCurrent() ||
				(spectator.getCurrent() && spectator.spectates(newSection))) &&
				newSection != oldSection

			let newView
			if (options.requireSuccess || navToNewSection) {
				// Trying to maintain an existing gardener while loading a new
				// tree in the same view is super complicated and not worth refactoring.
				// So for drilldowns we'll just create a new view we can swap in
				// if successful, and if not we just keep the current (unaltered) nav view.
				// If viewClass is an empty string, don't attempt to create a new view
				// (e.g. when handleNoCalendars() is called) so we don't get a js error
				if (!_.isEmpty(ViewClass)) {
					newView = new ViewClass(options)
				}
			}
			if (_.isNil(newView)) {
				resolve(currentView)
			} else {
				const options = {
					drilldown: this.drilldown
				}
				resolve(this._nav.displayNav(newView, options))
			}
		})
	},

	// returns the view currently in the nav pane
	navView: function() {
		return this._nav && this._nav.view
	},

	/**
	 * Replaces the view in the display pane with the provided view.
	 *
	 * @param {Backbone.View|jQuery.Deferred} viewOrPromise a view to display,
	 *     or a Deferred that returns a view
	 *
	 * @param {Function} [viewCreator] a function that creates the view and
	 *     returns it
	 *
	 * @param {Boolean} [delayViewRemove=false] When true, the app-display
	 *     delays the removal of the previous view slightly in order to make
	 *     for a smoother transition between the old and new views. The
	 *     parameter has no effect when false or missing.
	 *
	 * @returns {Promise<T>|undefined} A Promise that will be resolved with
	 * the view (if a promise was passed to this function), or undefined if a
	 *     view was passed
	 */
	display: function(viewOrPromise, viewCreator, delayViewRemove) {
		this._display.once('ready', (view, options) => {
			this._broadcastViewReady(view, options)
			this.drilldown = false
		})
		return this._display.display(viewOrPromise, viewCreator, {
			delayViewRemove,
			drilldown: this.drilldown
		})
	},

	/**
	 * Returns the view currently in the display pane
	 */
	displayView: function() {
		return this._display && this._display.view
	},

	/**
	 * Returns the id of the view currently in the display pane.  Typically
	 * undefined unless a view explicitly set's `view.domainObjectId` because it
	 * knows it's displaying something other than whats in the url.
	 * (e.g. Form pages)
	 */
	domainObjectId: function() {
		return _.result(this.displayView(), 'domainObjectId')
	},

	// replaces the view in the slide pane with the provided view
	slide: function(view) {
		this._slide.display(view)
	},

	// returns the view currently in the slide pane
	slideView: function() {
		return this._slide && this._slide.view
	},

	/**
	 * show a dialog containing the given view.  if there is already a dialog
	 * being displayed, the new one is stacked on top
	 * @param {Backbone.View|Promise|String} viewOrPromise
	 * 		If a promise, it must be fulfilled with a Backbone View
	 * 		If a string, it should be HTML (from a hbs template)
	 * @param {function} [viewCreator] required when a promise is provided.
	 * @param {object} [options]
	 * @param {boolean} [options.replace=false]
	 *
	 * @return the current dialog if a view was provided, or a promise for the
	 * dialog if a promise and viewCreator were provided
	 */
	dialog: function(viewOrPromise, viewCreator, options) {
		// trigger an event for tests to listen to
		Backbone.trigger('dialog:show', viewOrPromise, viewCreator, options)

		// display new dialog, if one is available
		if (viewOrPromise) {
			var replace = _.result(options, 'replace', false)
			var dialogPromise = this._showDialogForPromise(viewOrPromise, viewCreator, replace)
			if (!dialogPromise) {
				var view = viewOrPromise
				// #31277 don't want to replace alerts that have been popped up,
				// so find the top dialog that isn't an alert
				var current = dialogStack.peek({ ignoreAlerts: replace })
				if (replace && current) {
					current.content(view)
				} else {
					current = new Dialog({ content: view })
					this._pushDialog(current)
				}
				delete this.promise
				return current
			}
			return dialogPromise
		}
	},

	/**
	 * Closes the top dialog on the dialog stack. In most situations, it's better to
	 * trigger the 'done' or 'close' event on the view passed to the dialog, but
	 * it may be necessary to manually use this method in more complex dialog interactions.
	 * See dataset-template-form.js for an example.
	 */
	popDialog() {
		let dialog = dialogStack.peek()
		dialogStack.remove(dialog)
		dialog.close()
	},

	/**
	 * Used by both dialog() and replaceDialog() to properly display
	 * a view that is being generated via a promise.
	 *
	 * @param viewOrPromise
	 * @param viewCreator
	 * @param {boolean} [replace=false] replace any existing dialog instead of creating a new one
	 *
	 * @returns {boolean|Promise} false if a Backbone.View was passed, a promise for the dialog
	 * if a viewCreator was passed.
	 *
	 * @private
	 */
	_showDialogForPromise: function(viewOrPromise, viewCreator, replace) {
		var isNew = !dialogStack.peek() || !replace
		var isPromise = viewOrPromise && _.isFunction(viewOrPromise.then)
		if (isPromise) {
			this.promise = viewOrPromise
			isNew && this._pushDialog(new Dialog({ content: loadingDots() }))
			var loadingDialog = dialogStack.peek()
			loadingDialog.$el.addClass('dialog-with-loading-dots')

			// A promise that resolves with the created dialog object
			return new Promise((resolve, reject) => {
				viewOrPromise.then(function() {
					var view = viewCreator.apply(this, arguments)
						,	dialog = dialogStack.peek()
					dialog.$el.removeClass('dialog-with-loading-dots')
					dialog.content(view)
					resolve(dialog)
					// no need to keep these around anymore
					delete this.promise
				}.bind(this)) // Don't remove .bind(this) or use an arrow function here, or this will be undefined
				viewOrPromise.fail && viewOrPromise.fail(e => {
					loadingDialog.close()
					if (e && e.stack) {
						console.trace(e.stack)
					}
					reject(e)
				})
			})
		}
		return false
	},

	/**
	 * pushes a dialog onto the stack and applies appropriate class to previous
	 * dialogs to control how they are displayed in the background
	 * @param dialog
	 * @private
	 */
	_pushDialog: function(dialog) {
		dialogStack.push(dialog)
	},

	/**
	 * Replaces the top dialog's content with the given view. If no dialog is currently
	 * displayed, will open a new one
	 * @param viewOrPromise A Backbone.View or a promise that will return one
	 * @param {function} [viewCreator] required when a promise is provided.
	 */
	replaceDialog: function (viewOrPromise, viewCreator) {
		this.dialog(viewOrPromise, viewCreator, { replace: true })
	},

	// Clears all open dialogs. Generally should prefer dialogs closing naturally
	// via the content view emitting 'done'
	clearDialogs: function() {
		dialogStack.clear()
	},

	resize: function () {
		_.each([this._display, this._nav, this._slide], function (pane) { pane.resize() })
	},

	_broadcastViewReady: function(view, options) {
		if (layoutManager.export) {
			new ExportPreparer(view, options).prepare()
		}
		this.trigger('render:display', view)

		// Check if we're dealing with a view (bc, could also be a template),
		// and give it the ability to re-render the display pane and
		// adjust it's contents.
		if (view instanceof Backbone.View) {
			view.on('render:display', function() {
				this.trigger('render:display', view)
			}.bind(this))
		}
	}

}

_.extend(appDisplay, Backbone.Events)

// capture the current url before navigating so we can reset the url if needed
let _previousUrl
appDisplay.listenTo(Backbone, 'history:before-navigate', () => {
	_previousUrl = conductor.path()
})

export default appDisplay
export { Display }

appDisplay.__filename__ = 'components/app-display.js'
